import {
  Bar,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { BrStrategic } from '../../hooks/useBusinessReview'
import { formatKpiEur } from '../../lib/formatters'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BarType = 'positive' | 'negative' | 'total'

interface WaterfallEntry {
  name: string
  spacer: number
  value: number
  type: BarType
  raw: number // valore originale (con segno) per il tooltip
}

// ---------------------------------------------------------------------------
// Color map
// ---------------------------------------------------------------------------

const BAR_COLORS: Record<BarType, string> = {
  positive: '#22c55e',
  negative: '#ef4444',
  total: '#6366f1',
}

// ---------------------------------------------------------------------------
// buildWaterfallData — funzione pura esportata per i test
// ---------------------------------------------------------------------------

export function buildWaterfallData(strategic: BrStrategic): WaterfallEntry[] {
  const { booked_revenue_ytd: booked, waterfall } = strategic

  if (!waterfall || booked == null) return []

  const { nuovi_loghi, expansion, lost_value } = waterfall
  const lost = Math.abs(lost_value ?? 0)

  let cum = 0
  const data: WaterfallEntry[] = []

  if (nuovi_loghi > 0) {
    data.push({ name: 'Nuovi loghi', spacer: cum, value: nuovi_loghi, type: 'positive', raw: nuovi_loghi })
    cum += nuovi_loghi
  }

  if (expansion > 0) {
    data.push({ name: 'Expansion', spacer: cum, value: expansion, type: 'positive', raw: expansion })
    cum += expansion
  }

  if (lost > 0) {
    // La barra negativa parte dal basso (cum - lost) e sale fino a cum
    data.push({ name: 'Lost', spacer: cum - lost, value: lost, type: 'negative', raw: -lost })
    cum -= lost
  }

  data.push({ name: 'Booked', spacer: 0, value: booked, type: 'total', raw: booked })

  return data
}

// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------

interface TooltipProps {
  active?: boolean
  payload?: Array<{ payload: WaterfallEntry }>
}

function WaterfallTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null
  const entry = payload[0]?.payload
  if (!entry) return null

  const sign = entry.raw >= 0 ? '+' : ''
  const isTotal = entry.type === 'total'

  return (
    <div className="rounded-xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-[--km-text-primary] mb-1">{entry.name}</p>
      <p className="text-[--km-text-secondary]">
        {isTotal ? formatKpiEur(entry.raw) : `${sign}${formatKpiEur(Math.abs(entry.raw))}`}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// WaterfallChart
// ---------------------------------------------------------------------------

interface WaterfallChartProps {
  strategic: BrStrategic
}

export function WaterfallChart({ strategic }: WaterfallChartProps) {
  const data = buildWaterfallData(strategic)

  if (data.length === 0) {
    return (
      <div
        data-testid="waterfall-empty"
        className="flex items-center justify-center h-32 rounded-xl border border-dashed border-[var(--km-card-border)] text-xs text-[--km-text-secondary]"
      >
        Dati waterfall non disponibili per il periodo selezionato
      </div>
    )
  }

  const target = !strategic.target_not_configured ? strategic.annual_target : null

  return (
    <div data-testid="waterfall-chart" className="mt-4">
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: 'var(--km-text-secondary)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => `€${(v / 1000).toFixed(0)}k`}
            tick={{ fontSize: 10, fill: 'var(--km-text-secondary)' }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip content={<WaterfallTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />

          {target != null && (
            <ReferenceLine
              y={target}
              stroke="#f59e0b"
              strokeDasharray="4 3"
              label={{ value: 'Target', position: 'right', fontSize: 10, fill: '#f59e0b' }}
            />
          )}

          {/* Spacer trasparente — spinge la barra colorata in posizione */}
          <Bar dataKey="spacer" stackId="wf" fill="transparent" isAnimationActive={false} />

          {/* Barra colorata con Cell per colore per tipo */}
          <Bar dataKey="value" stackId="wf" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={BAR_COLORS[entry.type]} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legenda compatta */}
      <div className="flex items-center gap-4 mt-2 justify-center text-[10px] text-[--km-text-secondary]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#22c55e]" />Positivo</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#ef4444]" />Negativo</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#6366f1]" />Totale</span>
        {target != null && <span className="flex items-center gap-1"><span className="w-4 border-t border-dashed border-amber-400" />Target</span>}
      </div>
    </div>
  )
}
